"use client";

import React from "react";
import { Loader, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
function AccessFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-fade-in-up">
        <div className="bg-green-100 text-green-800 p-3 rounded-full mb-4">
          <CircleCheck className="size-8" />
        </div>
        <h3 className="text-2xl font-display font-bold mb-2">Request Received</h3>
        <p className="text-muted-foreground mb-6 max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="companyName" rules={{
        required: "Company name is required",
        minLength: {
          value: 2,
          message: "Must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Company Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter your company name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="contactPerson" rules={{
        required: "Contact person is required",
        minLength: {
          value: 2,
          message: "Must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Contact Person</FormLabel>
              <FormControl>
                <Input placeholder="Full name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="you@company.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        minLength: {
          value: 5,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Please provide some details about your request",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Additional Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your business and why you are requesting access..." className="min-h-[120px] resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3 text-destructive">
          <CircleAlert className="size-5 shrink-0 mt-0.5" />
          <p className="text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto btn-primary" size="lg">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Submitting Request...
          </> : "Request Access"}
      </Button>
    </Form>;
}
export default function RequestAccessForm() {
  return <section id="request-access-form" className="bg-background py-16 md:py-24 lg:py-32">
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        <div className="text-center mb-10 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
            Supplier Portal Access
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Complete the form below to request access to our supplier and buyer portals. Our team will review your application and respond within 1-2 business days.
          </p>
        </div>

        <Card className="border-border/50 shadow-medium">
          <CardHeader className="bg-muted/30 border-b border-border/50 pb-6">
            <CardTitle className="text-xl">Access Request Form</CardTitle>
            <CardDescription>
              All fields are required unless otherwise noted.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-8">
            <FormProvider formId="request_access_form_supplierandbuyerportals" sectionName="RequestAccessForm" defaultValues={{
            companyName: "",
            contactPerson: "",
            email: "",
            phone: "",
            message: ""
          }} submitText="Request Access" successMessage="Thank you for your request. Our team will review your information and contact you shortly with next steps.">
              <AccessFormInner />
            </FormProvider>
          </CardContent>
        </Card>
      </div>
    </section>;
}